import React, { useEffect, useState, useCallback } from "react";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../../../../components/Layout";
import Container from "../../../../../../components/Container";
import Flex from "../../../../../../components/Flex";
import Content from "../../../../../../components/Content";
import Button from "../../../../../../components/Button";
import Breadcrumbs from "../../../../../../components/Breadcrumbs";
import { useAuthState } from "../../../../../../components/AuthProvider";
import Spinner from "../../../../../../components/Loader/Spinner";

import styles from "./ViewSubscription.module.scss";

const ViewSubscription = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const organizationApiUrl = `${clientConfigApiDomain}/organizations`;
  const clientsApiUrl = `${clientConfigApiDomain}/clients`;
  const subscriptionApiUrl = `${clientConfigApiDomain}/subscriptions`;
  const matchModelApiUrl = `${clientConfigApiDomain}/match_models`;
  const clientSubscriptionsApiUrl = `${clientConfigApiDomain}/client_subscriptions`;

  const { id, clientId, subscriptionId } = useParams();

  const history = useHistory();
  const { user } = useAuthState();

  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [clientSubscriptionData, setClientSubscriptionData] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [matchModelData, setMatchModelData] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [organizationData, setOrganizationData] = useState([]);

  const fetchData = useCallback(async () => {
    window.scrollTo(0, 0);
    setIsLoading(true);

    if (subscriptionId !== undefined) {
      // get client subscription
      const response = await fetch(
        `${clientSubscriptionsApiUrl}/?id=${subscriptionId}`
      );
      const data = await response.json();
      setClientSubscriptionData(data.results[0]);
      // get subscription
      const respSub = await fetch(
        `${subscriptionApiUrl}/${data.results[0].subscription.id}/`
      );
      const dataSub = await respSub.json();
      setSubscriptionData(dataSub);
      // get match model
      const matchModelId = data.results[0].match_model.match(/\/([^/]+)\/?$/)[1];
      const respMatchModel = await fetch(
        `${matchModelApiUrl}/${matchModelId}/`
      );
      const dataMatchModel = await respMatchModel.json();
      setMatchModelData(dataMatchModel);
    }

    const clientResponse = await fetch(`${clientsApiUrl}/${clientId}/`);
    const clientData = await clientResponse.json();
    setClientData(clientData);

    const organizationResponse = await fetch(`${organizationApiUrl}/${id}/`);
    const organizationData = await organizationResponse.json();
    setOrganizationData(organizationData);

    setIsLoading(false);
    setHasLoaded(true);
  }, [
    subscriptionId,
    clientsApiUrl,
    clientId,
    organizationApiUrl,
    matchModelApiUrl,
    subscriptionApiUrl,
    id,
    clientSubscriptionsApiUrl
  ]);

  useEffect(() => {
    if (!hasLoaded) {
      fetchData();
    }
  }, [fetchData, hasLoaded]);

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = <Spinner />;
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => <Link to="/admin/organizations">Organizations</Link>}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}`}>
              Organization: {organizationData.name}
            </Link>
          )}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}/accounts/${clientId}`}>
              Account: {clientData.name}
            </Link>
          )}
        />
        <Breadcrumbs.Item
          last
          render={() => `Subscription: ${subscriptionData.name}`}
        />
      </Breadcrumbs>
    );
  }

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <span>Loading..</span>
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                <h1>
                  Subscription:{" "}
                  {subscriptionData.name ? subscriptionData.name : ""}
                </h1>
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>Subscription Details</h3>
                  <div>
                    {(user.role === "Global Admin" ||
                      user.orgIds.includes(organizationData.id)) && (
                      <Button
                        className={styles.margin}
                        primary
                        onClick={() => {
                          history.push(`${subscriptionId}/edit`);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
                <div className={styles.details}>
                  <div>
                    <h4>Subscription ID</h4>
                    <span>{clientSubscriptionData.id}</span>
                  </div>
                  <div>
                    <h4>Subscription</h4>
                    <span>{subscriptionData.name}</span>
                  </div>
                  <div>
                    <h4>Match Model</h4>
                    <span>{matchModelData.name}</span>
                  </div>
                  <div>
                    <h4>S3 Bucket</h4>
                    <span>
                      {clientSubscriptionData.s3_bucket_name
                        ? clientSubscriptionData.s3_bucket_name
                        : "-"}
                    </span>
                  </div>
                  <div>
                    <h4>Sink Kafka Topic</h4>
                    <span>
                      {clientSubscriptionData.sink_kafka_topic_name
                        ? clientSubscriptionData.sink_kafka_topic_name
                        : "-"}
                    </span>
                  </div>
                  <div>
                    <h4>Source Kafka Topic</h4>
                    <span>
                      {clientSubscriptionData.source_kafka_topic_name
                        ? clientSubscriptionData.source_kafka_topic_name
                        : "-"}
                    </span>
                  </div>
                  <div>
                    <h4>Entity Changes Kafka Topic</h4>
                    <span>
                      {clientSubscriptionData.entity_changes_kafka_topic_name
                        ? clientSubscriptionData.entity_changes_kafka_topic_name
                        : "-"}
                    </span>
                  </div>
                  <div>
                    <h4>Created By</h4>
                    <span>{clientSubscriptionData.created_by}</span>
                  </div>
                  <div>
                    <h4>Date Created</h4>
                    <span>
                      {moment(clientSubscriptionData.created_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                  <div>
                    <h4>Edited By</h4>
                    <span>{clientSubscriptionData.updated_by}</span>
                  </div>
                  <div>
                    <h4>Date Updated</h4>
                    <span>
                      {moment(clientSubscriptionData.updated_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default ViewSubscription;
